// «Dine data» på Din side (GDPR art. 15 og 20): kunden ser opplysningene vi har om dem,
// laster dem ned som fil eller får dem på e-post. Backend: src/routes/kundeInnsyn.js.
//
// Selvstendig: bruker bare /customer-portal/me/data og det den får inn (token, apiBase), så den
// kan flyttes til den nye kundeportalen som den er. Butikken sender kunden hit med
// /min-side?vis=mine-data (src/gdpr/innsyn.js); da åpnes avsnittet med en gang (apen).
import React, { useEffect, useId, useRef, useState } from 'react';

const PERSONVERN_URL = 'https://www.roykensykkelservice.no/personvern';
const RADER_FORST = 10;

const KNAPP = 'bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-50';
const SEKUNDAER = 'text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-xl px-4 py-2 disabled:opacity-50';
const FELT = 'w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-700';

const fmtDato = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric' });
};

function Avsnitt({ avsnitt }) {
  const [alle, setAlle] = useState(false);
  const rader = alle ? avsnitt.rader : avsnitt.rader.slice(0, RADER_FORST);
  const tittelId = `dinedata-${avsnitt.id}`;
  return (
    <section aria-labelledby={tittelId} className="border-t border-gray-200 pt-3">
      <h4 id={tittelId} className="font-semibold text-gray-900 text-sm">
        {avsnitt.tittel} <span className="font-normal text-gray-600">({avsnitt.rader.length})</span>
      </h4>
      {avsnitt.forklaring && <p className="text-xs text-gray-600 mt-0.5">{avsnitt.forklaring}</p>}
      <ul className="mt-2 space-y-1.5">
        {rader.map((r, i) => (
          <li key={i} className="text-sm text-gray-800 [overflow-wrap:anywhere]">
            {r.dato && <span className="text-gray-600 tabular-nums">{fmtDato(r.dato)}: </span>}
            {r.tekst}
          </li>
        ))}
      </ul>
      {avsnitt.rader.length > RADER_FORST && (
        <button type="button" onClick={() => setAlle((v) => !v)} className="mt-2 text-sm text-green-800 underline">
          {alle ? 'Vis færre' : `Vis alle ${avsnitt.rader.length}`}
        </button>
      )}
    </section>
  );
}

export default function DineData({ token, apiBase, apen = false, onNyInnlogging }) {
  const id = useId();
  const tittelRef = useRef(null);
  const [vis, setVis] = useState(apen);
  const [data, setData] = useState(null);
  const [laster, setLaster] = useState(false);
  const [feil, setFeil] = useState('');
  const [status, setStatus] = useState('');
  const [nyInnlogging, setNyInnlogging] = useState(false);
  // E-post: null | 'kode' (kode sendt) | 'sendt'
  const [epostSteg, setEpostSteg] = useState(null);
  const [kode, setKode] = useState('');
  const [opptatt, setOpptatt] = useState(false);

  const kall = async (metode, sti, body) => {
    const r = await fetch(`${apiBase}/customer-portal/me/data${sti}`, {
      method: metode,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...(body && { body: JSON.stringify(body) }),
    });
    if (r.status === 403) {
      const d = await r.json().catch(() => ({}));
      if (d.nyInnlogging) {
        setNyInnlogging(true);
        throw new Error(d.message);
      }
    }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.message || 'Noe gikk galt');
    }
    return r;
  };

  useEffect(() => {
    if (apen) tittelRef.current?.focus();
  }, [apen]);

  useEffect(() => {
    if (!vis || data) return;
    setLaster(true);
    kall('GET', '')
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setFeil(e.message))
      .finally(() => setLaster(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vis]);

  const lastNed = async () => {
    setFeil('');
    setStatus('');
    setOpptatt(true);
    try {
      const r = await kall('GET', '/fil');
      const navn = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'mine-data.json';
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = navn;
      a.click();
      URL.revokeObjectURL(url);
      setStatus('Filen er lastet ned.');
    } catch (e) {
      setFeil(e.message);
    } finally {
      setOpptatt(false);
    }
  };

  const sendKode = async () => {
    setFeil('');
    setStatus('');
    setOpptatt(true);
    try {
      const d = await (await kall('POST', '/epost', {})).json();
      setEpostSteg('kode');
      setKode('');
      setStatus(`Vi har sendt en kode til ${d.masked}.`);
    } catch (e) {
      setFeil(e.message);
    } finally {
      setOpptatt(false);
    }
  };

  const bekreft = async (e) => {
    e.preventDefault();
    setFeil('');
    setStatus('');
    setOpptatt(true);
    try {
      const d = await (await kall('POST', '/epost/bekreft', { kode: kode.trim() })).json();
      setEpostSteg('sendt');
      setStatus(`Dataene er sendt til ${d.masked}.`);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  return (
    <section aria-labelledby={`${id}-tittel`}>
      <h2 id={`${id}-tittel`} ref={tittelRef} tabIndex={-1} className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
        Dine data
      </h2>
      <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3">
        <p className="text-sm text-gray-800">
          Se opplysningene vi har om deg, last dem ned som fil, eller få dem på e-post.
        </p>
        {!vis && (
          <button type="button" onClick={() => setVis(true)} className={KNAPP}>Vis dataene mine</button>
        )}

        {nyInnlogging && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-900">
            <p>For å laste ned eller sende dataene må du ha logget inn de siste 30 minuttene.</p>
            <button type="button" onClick={onNyInnlogging} className={`${KNAPP} mt-2`}>Logg inn på nytt</button>
          </div>
        )}

        {vis && laster && <p className="text-sm text-gray-600" role="status">Henter dataene …</p>}

        {vis && data && (
          <>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={lastNed} disabled={opptatt} className={KNAPP}>Last ned som fil</button>
              {data.epost && epostSteg !== 'kode' && (
                <button type="button" onClick={sendKode} disabled={opptatt} className={SEKUNDAER}>Send til e-posten min</button>
              )}
            </div>
            {!data.epost && (
              <p className="text-xs text-gray-600">Vil du ha dataene på e-post? Legg til e-postadressen din under «Mine opplysninger» først.</p>
            )}
            {epostSteg === 'kode' && (
              <form onSubmit={bekreft} className="space-y-2">
                <label htmlFor={`${id}-kode`} className="block text-sm font-medium text-gray-900">Koden fra e-posten</label>
                <input
                  id={`${id}-kode`}
                  value={kode}
                  onChange={(e) => setKode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={FELT}
                />
                <div className="flex flex-wrap gap-2">
                  <button type="submit" disabled={opptatt || kode.length !== 6} className={KNAPP}>Send dataene</button>
                  <button type="button" onClick={() => setEpostSteg(null)} className={SEKUNDAER}>Avbryt</button>
                </div>
              </form>
            )}
            <p className="text-xs text-gray-600">
              Filen er i JSON-format, som kan leses av andre programmer. Oppbevar den trygt; den inneholder opplysninger om deg.
            </p>

            {data.kontakt?.length > 0 && (
              <section aria-labelledby={`${id}-kontakt`} className="border-t border-gray-200 pt-3">
                <h3 id={`${id}-kontakt`} className="font-semibold text-gray-900 text-sm">Kontaktopplysninger</h3>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  {data.kontakt.map((k) => (
                    <React.Fragment key={k.etikett}>
                      <dt className="text-gray-600">{k.etikett}</dt>
                      <dd className="text-gray-900 [overflow-wrap:anywhere]">{k.verdi}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            )}
            {data.avsnitt?.length > 0 && (
              <div className="space-y-3">
                <h3 className="sr-only">Det vi har registrert</h3>
                {data.avsnitt.map((a) => <Avsnitt key={a.id} avsnitt={a} />)}
              </div>
            )}

            <section aria-labelledby={`${id}-om`} className="border-t border-gray-200 pt-3 text-sm text-gray-800 space-y-1.5">
              <h3 id={`${id}-om`} className="font-semibold text-gray-900">Om opplysningene</h3>
              <p>Vi bruker dem til service, bookinger, oppbevaring og kjøp du har bestilt, og til å følge bokføringsloven.</p>
              <p>Regnskapsopplysninger oppbevarer vi i 5 år etter regnskapsåret. SMS- og e-posthistorikk slettes etter 24 måneder.</p>
              <p>
                Her ser du det som er koblet til kundekortet ditt. Eldre opplysninger som ikke er det, for eksempel chat eller kjøp
                i nettbutikken, kan butikken ta ut for deg.
              </p>
              <p>
                Du kan be oss rette eller slette opplysninger, og du kan klage til Datatilsynet. Mer i{' '}
                <a href={PERSONVERN_URL} className="text-green-800 underline">personvernerklæringen</a>.
              </p>
            </section>
          </>
        )}

        {feil && !nyInnlogging && <p className="text-red-700 text-sm" role="alert">{feil}</p>}
        <p className="text-sm text-gray-700" role="status">{status}</p>
      </div>
    </section>
  );
}
